import type { EventParticipant, EventParticipantRole } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetUserNameWithFallback } from 'features/users/hooks';
import { useGetEventRoleKey } from '../../../../../hooks';
import { useUpdateEventParticipant } from '../../../../../queries';

export const useUpdateEventInviteeRole = () => {
  const { t } = useTranslation();
  const { isPending: isLoadingChangingRole, mutateAsync: updateEventInviteeRole } = useUpdateEventParticipant();
  const getRoleName = useGetEventRoleKey();
  const getUserNameWithFallback = useGetUserNameWithFallback();

  const updateRole = useCallback(
    async ({ eventId, invitee, role }: { eventId: string; invitee: EventParticipant; role: EventParticipantRole }) =>
      updateEventInviteeRole(
        {
          eventId,
          participantRef: invitee.ref ?? invitee.email,
          role,
        },
        {
          onError() {
            message.error(
              t('Something went wrong. {{name}}’s role couldn’t be changed.', {
                name: getUserNameWithFallback(invitee),
              }),
            );
          },
          onSuccess() {
            message.success(
              t('{{name}}’s role changed to {{role}}', {
                name: getUserNameWithFallback(invitee),
                role: getRoleName({ role }),
              }),
            );
          },
        },
      ),
    [getRoleName, getUserNameWithFallback, t, updateEventInviteeRole],
  );

  return useMemo(() => ({ isLoadingChangingRole, updateRole }), [isLoadingChangingRole, updateRole]);
};
